import type { ForwardedRef, MouseEvent } from 'react'
import { useRef, forwardRef } from 'react'
import { getIdWithUniqueFallback } from '@getcircuit/web-utils'

import type { InputProps } from './types'
import { ErrorIcon, InputWrapper, InputSlot, StyledInput } from './Input.styled'
import { useForkRef } from '../../hooks'

export const SUPPORTED_TYPES = [
  'text',
  'password',
  'tel',
  'date',
  'email',
  'url',
  'search',
  'number',
] as const

function Input(
  {
    error,
    startSlot,
    endSlot,
    id: idProp,
    className,
    'aria-describedby': ariaDescribedByProp,
    ...rest
  }: InputProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const inputId = getIdWithUniqueFallback([idProp, rest.name], 'input')
  const startSlotId = startSlot ? `${inputId}-start-slot` : undefined
  const endSlotId = endSlot ? `${inputId}-end-slot` : undefined

  const inputRef = useRef<HTMLInputElement>()
  const forkedRef = useForkRef(ref, inputRef)

  /** ids of descriptors */
  let ariaDescribedBy = ariaDescribedByProp

  // Use text stard|end slots as descriptors as well
  if (startSlot || endSlot) {
    const ids = [
      ariaDescribedByProp,
      typeof startSlot === 'string' && startSlotId,
      typeof endSlot === 'string' && endSlotId,
    ].filter(Boolean)

    if (ids.length > 0) {
      ariaDescribedBy = ids.join(' ')
    }
  }

  const handleWrapperClick = (e: MouseEvent) => {
    // if user clicked on the wrapper itself, focus the input
    if (e.currentTarget === e.target) {
      inputRef.current?.focus()
    }
  }

  return (
    <InputWrapper
      className={className}
      $hasError={error}
      $disabled={rest.disabled}
      onClick={handleWrapperClick}
    >
      {startSlot && (
        <InputSlot id={startSlotId} $content={startSlot}>
          {startSlot}
        </InputSlot>
      )}

      <StyledInput
        ref={forkedRef}
        $hasStartSlot={startSlot}
        $hasEndSlot={endSlot ?? error}
        $hasError={error}
        id={inputId}
        aria-describedby={ariaDescribedBy}
        {...rest}
      />

      {(error || endSlot) && (
        <InputSlot id={endSlotId} $content={endSlot}>
          {error && (
            <ErrorIcon
              title="input-error-icon"
              aria-hidden
              $hasEndSlot={endSlot}
            />
          )}
          {endSlot}
        </InputSlot>
      )}
    </InputWrapper>
  )
}

export default forwardRef(Input)
